import React, { useState, useEffect } from 'react';
import {
  Container, Row, Col, Card, Form, Button, Table,
  Modal, Badge, Alert, Spinner, Image,
  InputGroup, FormControl, Accordion
} from 'react-bootstrap';
import axios from 'axios';
import AdminNavbar from './components/AdminNavbar';
import { BASE_URL, UPLOAD_BASE_URL } from '../../services/api';

const AnnouncementsManager = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [user, setUser] = useState(null);
  
  // Form states
  const [showModal, setShowModal] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  
  // Options for icons, colors, pages, and sizes
  const [availableIcons, setAvailableIcons] = useState([]);
  const [availableColors, setAvailableColors] = useState([]);
  const [availableSizes, setAvailableSizes] = useState([]);
  const [availablePages, setAvailablePages] = useState([]);
  
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    attachment: '',
    icon: 'fas fa-bullhorn',
    icon_color: 'danger',
    date: new Date().toISOString().split('T')[0], // Today's date
    is_active: true,
    // New fields for popup functionality
    pages: [], // Array of page slugs
    show_popup: true,
    popup_size: 'medium',
    popup_duration: 0,
    dismissible: true,
    show_once_per_session: false,
    custom_css: '',
    start_date: '',
    end_date: '',
    priority: 0
  });

  const API_BASE_URL = BASE_URL;
  
  useEffect(() => {
    // Fetch user data from localStorage or API
    const userData = JSON.parse(localStorage.getItem('user') || 'null');
    setUser(userData);
    
    fetchAnnouncements();
    fetchOptions();
  }, []);

  const resetForm = () => {
    setFormData({
      title: '',
      content: '',
      attachment: '',
      icon: 'fas fa-bullhorn',
      icon_color: 'danger',
      date: new Date().toISOString().split('T')[0],
      is_active: true,
      pages: [],
      show_popup: true,
      popup_size: 'medium',
      popup_duration: 0,
      dismissible: true,
      show_once_per_session: false,
      custom_css: '',
      start_date: '',
      end_date: '',
      priority: 0
    });
  };

  const handleLogout = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    window.location.href = '/admin/login';
  };

  const fetchAnnouncements = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('token');
      
      const response = await axios.get(`${API_BASE_URL}/announcements.php?admin=true`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.data.success) {
        const processedAnnouncements = response.data.data.map(announcement => ({
          ...announcement,
          id: parseInt(announcement.id),
          is_active: announcement.is_active === "1" || announcement.is_active === 1,
          show_popup: announcement.show_popup === "1" || announcement.show_popup === 1,
          dismissible: announcement.dismissible === "1" || announcement.dismissible === 1,
          show_once_per_session: announcement.show_once_per_session === "1" || announcement.show_once_per_session === 1,
          pages: announcement.pages ? (typeof announcement.pages === 'string' ? JSON.parse(announcement.pages) : announcement.pages) : [],
          priority: parseInt(announcement.priority) || 0,
          popup_duration: parseInt(announcement.popup_duration) || 0
        }));
        
        setAnnouncements(processedAnnouncements);
      } else {
        setError('Failed to load announcements');
      }
    } catch (err) {
      setError('Error connecting to server');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchOptions = async () => {
    try {
      setOptionsLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.get(`${API_BASE_URL}/announcements.php?options=true`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.data.success) {
        const data = response.data.data;
        setAvailableIcons(data.icons || []);
        setAvailableColors(data.colors || {});
        setAvailableSizes(data.sizes || {});
        setAvailablePages(data.pages || {});
      }
    } catch (err) {
      console.error('Error fetching options:', err);
    } finally {
      setOptionsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleTextareaChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({
        ...prev,
        attachment_file: file
      }));
    }
  };

  const handlePageSelect = (pageSlug) => {
    setFormData(prev => {
      const currentPages = [...prev.pages];
      if (currentPages.includes(pageSlug)) {
        return { ...prev, pages: currentPages.filter(p => p !== pageSlug) };
      } else {
        return { ...prev, pages: [...currentPages, pageSlug] };
      }
    });
  };

  const selectAllPages = () => {
    setFormData(prev => ({
      ...prev,
      pages: Object.keys(availablePages)
    }));
  };

  const clearAllPages = () => {
    setFormData(prev => ({
      ...prev,
      pages: []
    }));
  };

  const removeAttachment = () => {
    setFormData(prev => ({
      ...prev,
      attachment: '',
      attachment_file: null
    }));
  };

  const uploadAttachment = async (file) => {
    try {
      setUploadingAttachment(true);
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('attachment', file);
      
      const response = await axios.post(`${API_BASE_URL}/upload_attachment.php`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.data.success) {
        return response.data.attachment_url;
      }
      return null;
    } catch (err) {
      console.error('Attachment upload error:', err);
      return null;
    } finally {
      setUploadingAttachment(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      let dataToSend = { ...formData };
      
      // Remove attachment_file from dataToSend if it exists
      if (dataToSend.attachment_file) {
        delete dataToSend.attachment_file;
      }
      
      // Convert boolean values to integers for backend
      dataToSend.is_active = dataToSend.is_active ? 1 : 0;
      dataToSend.show_popup = dataToSend.show_popup ? 1 : 0;
      dataToSend.dismissible = dataToSend.dismissible ? 1 : 0;
      dataToSend.show_once_per_session = dataToSend.show_once_per_session ? 1 : 0;
      
      // Convert priority to integer
      dataToSend.priority = parseInt(dataToSend.priority) || 0;
      dataToSend.popup_duration = parseInt(dataToSend.popup_duration) || 0;
      
      // If there's a new attachment to upload
      if (formData.attachment_file) {
        const uploadedUrl = await uploadAttachment(formData.attachment_file);
        if (uploadedUrl) {
          dataToSend.attachment = uploadedUrl;
        } else {
          setError('Failed to upload attachment');
          return;
        }
      }
      
      const token = localStorage.getItem('token');
      let response;
      
      if (editingAnnouncement) {
        response = await axios.put(
          `${API_BASE_URL}/announcements.php?id=${editingAnnouncement.id}`,
          dataToSend,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
      } else {
        response = await axios.post(`${API_BASE_URL}/announcements.php`, dataToSend, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
      }
      
      if (response.data.success) {
        setSuccess(editingAnnouncement ? 'Announcement updated!' : 'Announcement added!');
        setShowModal(false);
        resetForm();
        setEditingAnnouncement(null);
        fetchAnnouncements();
        
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError('Operation failed: ' + (response.data.message || ''));
      }
    } catch (err) {
      setError('Error saving announcement: ' + (err.response?.data?.message || err.message));
      console.error(err);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this announcement?')) {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.delete(`${API_BASE_URL}/announcements.php?id=${id}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.data.success) {
          setSuccess('Announcement deleted!');
          fetchAnnouncements();
          setTimeout(() => setSuccess(''), 3000);
        } else {
          setError('Failed to delete announcement: ' + (response.data.message || ''));
        }
      } catch (err) {
        setError('Error deleting announcement');
        console.error(err);
      }
    }
  };

  const handleEdit = (announcement) => {
    setEditingAnnouncement(announcement);
    setFormData({
      title: announcement.title,
      content: announcement.content || '',
      attachment: announcement.attachment || '',
      icon: announcement.icon || 'fas fa-bullhorn',
      icon_color: announcement.icon_color || 'danger',
      date: announcement.date,
      is_active: announcement.is_active === true || announcement.is_active === "1" || announcement.is_active === 1,
      pages: announcement.pages || [],
      show_popup: announcement.show_popup === true || announcement.show_popup === "1" || announcement.show_popup === 1,
      popup_size: announcement.popup_size || 'medium',
      popup_duration: announcement.popup_duration || 0,
      dismissible: announcement.dismissible === true || announcement.dismissible === "1" || announcement.dismissible === 1,
      show_once_per_session: announcement.show_once_per_session === true || announcement.show_once_per_session === "1" || announcement.show_once_per_session === 1,
      custom_css: announcement.custom_css || '',
      start_date: announcement.start_date || '',
      end_date: announcement.end_date || '',
      priority: announcement.priority || 0
    });
    
    setShowModal(true);
  };

  const handleToggleActive = async (announcement) => {
    try {
      const currentActive = announcement.is_active === true || announcement.is_active === "1" || announcement.is_active === 1;
      const newActiveStatus = !currentActive;
      
      const updatedData = {
        title: announcement.title,
        content: announcement.content || '',
        attachment: announcement.attachment || '',
        icon: announcement.icon || 'fas fa-bullhorn',
        icon_color: announcement.icon_color || 'danger',
        date: announcement.date,
        is_active: newActiveStatus ? 1 : 0,
        pages: announcement.pages || [],
        show_popup: announcement.show_popup === true || announcement.show_popup === "1" || announcement.show_popup === 1 ? 1 : 0,
        popup_size: announcement.popup_size || 'medium',
        popup_duration: announcement.popup_duration || 0,
        dismissible: announcement.dismissible === true || announcement.dismissible === "1" || announcement.dismissible === 1 ? 1 : 0,
        show_once_per_session: announcement.show_once_per_session === true || announcement.show_once_per_session === "1" || announcement.show_once_per_session === 1 ? 1 : 0,
        custom_css: announcement.custom_css || '',
        start_date: announcement.start_date || '',
        end_date: announcement.end_date || '',
        priority: announcement.priority || 0
      };
      
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API_BASE_URL}/announcements.php?id=${announcement.id}`,
        updatedData,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      if (response.data.success) {
        fetchAnnouncements();
      } else {
        setError('Failed to update status: ' + (response.data.message || ''));
      }
    } catch (err) {
      console.error('Error toggling active status:', err);
      setError('Failed to update status');
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingAnnouncement(null);
    resetForm();
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatSimpleDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const truncateText = (text, maxLength = 100) => {
    if (!text) return 'No content';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const getFileIcon = (filename) => {
    if (!filename) return 'fas fa-file';
    const ext = filename.split('.').pop().toLowerCase();
    switch (ext) {
      case 'pdf': return 'fas fa-file-pdf text-danger';
      case 'doc':
      case 'docx': return 'fas fa-file-word text-primary';
      case 'txt': return 'fas fa-file-alt text-secondary';
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif': return 'fas fa-file-image text-success';
      default: return 'fas fa-file';
    }
  };

  const getPopupSizeLabel = (size) => {
    return availableSizes[size] || size;
  };

  const getPageNames = (pageSlugs) => {
    if (!pageSlugs || pageSlugs.length === 0) return 'All Pages';
    return pageSlugs.map(slug => availablePages[slug] || slug).join(', ');
  };

  if (loading) {
    return (
      <>
        <AdminNavbar onLogout={handleLogout} user={user} />
        <Container className="mt-5 text-center">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Loading announcements...</p>
        </Container>
      </>
    );
  }

  return (
    <>
      <AdminNavbar onLogout={handleLogout} user={user} />
      <Container fluid className="py-4">
        <Row className="mb-4">
          <Col>
            <h2>Announcements Manager</h2>
            <p className="text-muted">Manage website announcements with page-specific popups</p>
          </Col>
          <Col className="text-end">
            <Button variant="primary" onClick={() => {
              setShowModal(true);
            }}>
              <i className="fas fa-plus me-2"></i>Add Announcement
            </Button>
          </Col>
        </Row>

        {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
        {success && <Alert variant="success" onClose={() => setSuccess('')} dismissible>{success}</Alert>}

        <Card>
          <Card.Body>
            <Table responsive hover>
              <thead>
                <tr>
                  <th width="60">Icon</th>
                  <th>Title</th>
                  <th>Content Preview</th>
                  <th>Pages</th>
                  <th>Popup</th>
                  <th>Priority</th>
                  <th>Status</th>
                  <th>Schedule</th>
                  <th width="150">Actions</th>
                </tr>
              </thead>
              <tbody>
                {announcements.map((announcement) => (
                  <tr key={announcement.id}>
                    <td>
                      <div className={`text-${announcement.icon_color}`}>
                        <i className={`${announcement.icon} fa-lg`}></i>
                      </div>
                    </td>
                    <td>
                      <strong>{announcement.title}</strong>
                      <div className="text-muted small">
                        {formatSimpleDate(announcement.date)}
                      </div>
                    </td>
                    <td>
                      <small className="text-muted">
                        {truncateText(announcement.content, 40)}
                      </small>
                    </td>
                    <td>
                      <Badge bg="info" className="mb-1">
                        <i className="fas fa-file-alt me-1"></i>
                        {announcement.pages && announcement.pages.length > 0 
                          ? `${announcement.pages.length} page${announcement.pages.length !== 1 ? 's' : ''}`
                          : 'All Pages'}
                      </Badge>
                    </td>
                    <td>
                      {announcement.show_popup ? (
                        <Badge bg="success">
                          <i className="fas fa-external-link-alt me-1"></i>
                          {getPopupSizeLabel(announcement.popup_size)}
                          {announcement.popup_duration > 0 && ` (${announcement.popup_duration}s)`}
                        </Badge>
                      ) : (
                        <Badge bg="secondary">List Only</Badge>
                      )}
                    </td>
                    <td>
                      <Badge bg="warning">
                        {announcement.priority}
                      </Badge>
                    </td>
                    <td>
                      <div className="d-flex flex-column">
                        <Form.Check
                          type="switch"
                          checked={announcement.is_active === true || announcement.is_active === "1" || announcement.is_active === 1}
                          onChange={() => handleToggleActive(announcement)}
                          label={(announcement.is_active === true || announcement.is_active === "1" || announcement.is_active === 1) ? 'Active' : 'Inactive'}
                          key={`active-${announcement.id}-${announcement.is_active}`}
                        />
                        {announcement.attachment && (
                          <small>
                            <i className="fas fa-paperclip me-1"></i>
                            Has attachment
                          </small>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="small">
                        {announcement.start_date && (
                          <div className="text-success">
                            <i className="fas fa-play me-1"></i>
                            {formatSimpleDate(announcement.start_date)}
                          </div>
                        )}
                        {announcement.end_date && (
                          <div className="text-danger">
                            <i className="fas fa-stop me-1"></i>
                            {formatSimpleDate(announcement.end_date)}
                          </div>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="d-flex flex-column gap-1">
                        <Button
                          size="sm"
                          variant="outline-primary"
                          onClick={() => handleEdit(announcement)}
                        >
                          <i className="fas fa-edit me-1"></i>
                          Edit
                        </Button>
                        
                        <Button
                          size="sm"
                          variant="outline-danger"
                          onClick={() => handleDelete(announcement.id)}
                        >
                          <i className="fas fa-trash me-1"></i>
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            
            {announcements.length === 0 && (
              <div className="text-center py-5 text-muted">
                <i className="fas fa-bullhorn fa-3x mb-3"></i>
                <p>No announcements found. Add your first announcement!</p>
              </div>
            )}
          </Card.Body>
        </Card>

        {/* Add/Edit Announcement Modal */}
        <Modal show={showModal} onHide={handleCloseModal} size="xl">
          <Modal.Header closeButton>
            <Modal.Title>
              {editingAnnouncement ? 'Edit Announcement' : 'Add Announcement'}
              {formData.show_popup && (
                <Badge bg="success" className="ms-2">
                  <i className="fas fa-external-link-alt me-1"></i>
                  Popup Announcement
                </Badge>
              )}
            </Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleSubmit}>
            <Modal.Body>
              <Accordion defaultActiveKey={['0', '1']} alwaysOpen>
                {/* Basic Information */}
                <Accordion.Item eventKey="0">
                  <Accordion.Header>
                    <i className="fas fa-info-circle me-2"></i>
                    Basic Information
                  </Accordion.Header>
                  <Accordion.Body>
                    <Row>
                      <Col md={8}>
                        <Form.Group className="mb-3">
                          <Form.Label>Title *</Form.Label>
                          <Form.Control
                            type="text"
                            name="title"
                            value={formData.title}
                            onChange={handleInputChange}
                            required
                            placeholder="Enter announcement title"
                          />
                        </Form.Group>
                      </Col>
                      
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>Date *</Form.Label>
                          <Form.Control
                            type="date"
                            name="date"
                            value={formData.date}
                            onChange={handleInputChange}
                            required
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    
                    <Form.Group className="mb-3">
                      <Form.Label>Content *</Form.Label>
                      <Form.Control
                        as="textarea"
                        name="content"
                        value={formData.content}
                        onChange={handleTextareaChange}
                        rows={8}
                        required
                        placeholder="Enter announcement content (HTML allowed)..."
                      />
                      <Form.Text className="text-muted">
                        You can use HTML tags for formatting (e.g., &lt;strong&gt;, &lt;em&gt;, &lt;br&gt;)
                      </Form.Text>
                    </Form.Group>
                    
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Icon</Form.Label>
                          {optionsLoading ? (
                            <Spinner size="sm" animation="border" />
                          ) : (
                            <Form.Select
                              name="icon"
                              value={formData.icon}
                              onChange={handleInputChange}
                            >
                              {availableIcons.map((icon, index) => (
                                <option key={index} value={icon}>
                                  <i className={`${icon} me-2`}></i>
                                  {icon.replace('fas fa-', '').replace(/-/g, ' ')}
                                </option>
                              ))}
                            </Form.Select>
                          )}
                        </Form.Group>
                      </Col>
                      
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Icon Color</Form.Label>
                          {optionsLoading ? (
                            <Spinner size="sm" animation="border" />
                          ) : (
                            <Form.Select
                              name="icon_color"
                              value={formData.icon_color}
                              onChange={handleInputChange}
                            >
                              {Object.entries(availableColors).map(([value, label]) => (
                                <option key={value} value={value}>
                                  <Badge bg={value}>{label}</Badge>
                                </option>
                              ))}
                            </Form.Select>
                          )}
                        </Form.Group>
                      </Col>
                    </Row>
                    
                    <Row>
                      <Col md={8}>
                        <Form.Group className="mb-3">
                          <Form.Label>Attachment</Form.Label>
                          <Form.Control
                            type="file"
                            onChange={handleFileChange}
                            disabled={uploadingAttachment}
                            accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.gif"
                          />
                          <Form.Text className="text-muted">
                            Upload PDF, DOC, TXT, or image files (optional)
                          </Form.Text>
                        </Form.Group>
                      </Col>
                      
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>Priority</Form.Label>
                          <Form.Control
                            type="number"
                            name="priority"
                            value={formData.priority}
                            onChange={handleInputChange}
                            min="0"
                            max="100"
                          />
                          <Form.Text className="text-muted">
                            Higher number shows first (0-100)
                          </Form.Text>
                        </Form.Group>
                      </Col>
                    </Row>
                    
                    {/* Current attachment preview */}
                    {formData.attachment && (
                      <div className="mb-3">
                        <Form.Label>Current Attachment</Form.Label>
                        <div className="d-flex align-items-center">
                          <Badge bg="secondary" className="me-2">
                            <i className={`${getFileIcon(formData.attachment)} me-1`}></i>
                            Attached
                          </Badge>
                          
                          <a 
                            href={`${UPLOAD_BASE_URL}${formData.attachment}`} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="me-2"
                          >
                            <small>View</small>
                          </a>
                          
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={removeAttachment}
                          >
                            <i className="fas fa-trash me-1"></i> Remove
                          </Button>
                        </div>
                      </div>
                    )}
                  </Accordion.Body>
                </Accordion.Item>

                {/* Popup Settings */}
                <Accordion.Item eventKey="1">
                  <Accordion.Header>
                    <i className="fas fa-external-link-alt me-2"></i>
                    Popup Settings
                  </Accordion.Header>
                  <Accordion.Body>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Check
                            type="switch"
                            name="show_popup"
                            label="Show as Popup"
                            checked={formData.show_popup}
                            onChange={handleInputChange}
                          />
                          <Form.Text className="text-muted">
                            If unchecked, announcement will only appear in announcements list
                          </Form.Text>
                        </Form.Group>
                      </Col>
                      
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Check
                            type="switch"
                            name="is_active"
                            label="Active"
                            checked={formData.is_active}
                            onChange={handleInputChange}
                          />
                          <Form.Text className="text-muted">
                            Inactive announcements won't be shown anywhere
                          </Form.Text>
                        </Form.Group>
                      </Col>
                    </Row>

                    {formData.show_popup && (
                      <>
                        <Row>
                          <Col md={4}>
                            <Form.Group className="mb-3">
                              <Form.Label>Popup Size</Form.Label>
                              {optionsLoading ? (
                                <Spinner size="sm" animation="border" />
                              ) : (
                                <Form.Select
                                  name="popup_size"
                                  value={formData.popup_size}
                                  onChange={handleInputChange}
                                >
                                  {Object.entries(availableSizes).map(([value, label]) => (
                                    <option key={value} value={value}>
                                      {label}
                                    </option>
                                  ))}
                                </Form.Select>
                              )}
                            </Form.Group>
                          </Col>
                          
                          <Col md={4}>
                            <Form.Group className="mb-3">
                              <Form.Label>Auto-close Duration (seconds)</Form.Label>
                              <Form.Control
                                type="number"
                                name="popup_duration"
                                value={formData.popup_duration}
                                onChange={handleInputChange}
                                min="0"
                                max="3600"
                              />
                              <Form.Text className="text-muted">
                                0 = no auto-close
                              </Form.Text>
                            </Form.Group>
                          </Col>
                          
                          <Col md={4}>
                            <Form.Group className="mb-3">
                              <Form.Label>Pages to Show On</Form.Label>
                              <div className="mb-2">
                                <Button
                                  size="sm"
                                  variant="outline-secondary"
                                  onClick={selectAllPages}
                                  className="me-2"
                                >
                                  Select All
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline-secondary"
                                  onClick={clearAllPages}
                                >
                                  Clear All
                                </Button>
                              </div>
                            </Form.Group>
                          </Col>
                        </Row>

                        <Row>
                          <Col md={4}>
                            <Form.Group className="mb-3">
                              <Form.Check
                                type="switch"
                                name="dismissible"
                                label="Dismissible"
                                checked={formData.dismissible}
                                onChange={handleInputChange}
                              />
                              <Form.Text className="text-muted">
                                User can close the popup
                              </Form.Text>
                            </Form.Group>
                          </Col>
                          
                          <Col md={4}>
                            <Form.Group className="mb-3">
                              <Form.Check
                                type="switch"
                                name="show_once_per_session"
                                label="Show Once Per Session"
                                checked={formData.show_once_per_session}
                                onChange={handleInputChange}
                              />
                              <Form.Text className="text-muted">
                                Only show once per browser session
                              </Form.Text>
                            </Form.Group>
                          </Col>
                        </Row>

                        {/* Page Selection */}
                        {availablePages && Object.keys(availablePages).length > 0 && (
                          <Form.Group className="mb-3">
                            <Form.Label>Select Pages (leave empty for all pages)</Form.Label>
                            <div className="border rounded p-3 bg-light">
                              <Row>
                                {Object.entries(availablePages).map(([slug, name]) => (
                                  <Col md={4} key={slug}>
                                    <Form.Check
                                      type="checkbox"
                                      id={`page-${slug}`}
                                      label={name}
                                      checked={formData.pages.includes(slug)}
                                      onChange={() => handlePageSelect(slug)}
                                      className="mb-2"
                                    />
                                  </Col>
                                ))}
                              </Row>
                              <div className="mt-2 text-muted small">
                                <i className="fas fa-info-circle me-1"></i>
                                Selected: {formData.pages.length} page{formData.pages.length !== 1 ? 's' : ''}
                                {formData.pages.length === 0 && ' (All pages)'}
                              </div>
                            </div>
                          </Form.Group>
                        )}

                        {/* Schedule Settings */}
                        <h6 className="mt-4 mb-3">
                          <i className="fas fa-calendar-alt me-2"></i>
                          Schedule Settings (Optional)
                        </h6>
                        <Row>
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label>Start Date/Time</Form.Label>
                              <Form.Control
                                type="datetime-local"
                                name="start_date"
                                value={formData.start_date}
                                onChange={handleInputChange}
                              />
                              <Form.Text className="text-muted">
                                When to start showing this announcement
                              </Form.Text>
                            </Form.Group>
                          </Col>
                          
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label>End Date/Time</Form.Label>
                              <Form.Control
                                type="datetime-local"
                                name="end_date"
                                value={formData.end_date}
                                onChange={handleInputChange}
                              />
                              <Form.Text className="text-muted">
                                When to stop showing this announcement
                              </Form.Text>
                            </Form.Group>
                          </Col>
                        </Row>

                        {/* Custom CSS */}
                        <Form.Group className="mb-3">
                          <Form.Label>Custom CSS</Form.Label>
                          <Form.Control
                            as="textarea"
                            name="custom_css"
                            value={formData.custom_css}
                            onChange={handleTextareaChange}
                            rows={4}
                            placeholder="Enter custom CSS for the popup (JSON format)"
                          />
                          <Form.Text className="text-muted">
                            Example: {"{"}"color": "red", "backgroundColor": "#f8f9fa"{"}"}
                          </Form.Text>
                        </Form.Group>
                      </>
                    )}
                  </Accordion.Body>
                </Accordion.Item>

                {/* Preview */}
                <Accordion.Item eventKey="2">
                  <Accordion.Header>
                    <i className="fas fa-eye me-2"></i>
                    Preview
                  </Accordion.Header>
                  <Accordion.Body>
                    <Card className="border">
                      <Card.Header className="d-flex align-items-center">
                        <i className={`${formData.icon} text-${formData.icon_color} me-2 fs-4`}></i>
                        <strong>{formData.title || 'Announcement Title'}</strong>
                        <Badge bg={formData.is_active ? 'success' : 'secondary'} className="ms-3">
                          {formData.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                        {formData.show_popup && (
                          <Badge bg="info" className="ms-2">
                            <i className="fas fa-external-link-alt me-1"></i>
                            Popup
                          </Badge>
                        )}
                      </Card.Header>
                      <Card.Body>
                        {formData.content ? (
                          <div dangerouslySetInnerHTML={{ __html: formData.content }} />
                        ) : (
                          <p className="text-muted">Announcement content will appear here...</p>
                        )}
                        
                        <div className="mt-3 pt-3 border-top">
                          <Row>
                            <Col md={6}>
                              <div className="small text-muted">
                                <i className="fas fa-calendar me-1"></i>
                                Date: {formData.date || 'Not set'}
                              </div>
                              {formData.pages && formData.pages.length > 0 && (
                                <div className="small text-muted">
                                  <i className="fas fa-file-alt me-1"></i>
                                  Pages: {getPageNames(formData.pages)}
                                </div>
                              )}
                            </Col>
                            <Col md={6}>
                              {formData.attachment && (
                                <div className="small text-muted">
                                  <i className="fas fa-paperclip me-1"></i>
                                  Has attachment
                                </div>
                              )}
                              <div className="small text-muted">
                                <i className="fas fa-sort-numeric-up me-1"></i>
                                Priority: {formData.priority}
                              </div>
                            </Col>
                          </Row>
                        </div>
                      </Card.Body>
                    </Card>
                  </Accordion.Body>
                </Accordion.Item>
              </Accordion>

              {editingAnnouncement && (
                <div className="alert alert-info mt-3">
                  <small>
                    <i className="fas fa-info-circle me-2"></i>
                    Created: {formatDate(editingAnnouncement.created_at)} | 
                    Last Updated: {formatDate(editingAnnouncement.updated_at || editingAnnouncement.created_at)}
                  </small>
                </div>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleCloseModal}>
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={uploadingAttachment}>
                {uploadingAttachment && <Spinner animation="border" size="sm" className="me-2" />}
                {editingAnnouncement ? 'Update' : 'Add'} Announcement
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
      </Container>
    </>
  );
};

export default AnnouncementsManager;